import { forwardRef, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    addDays,
    addMonths,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    isBefore,
    isSameDay,
    isSameMonth,
    set,
    startOfDay,
    startOfMonth,
    startOfWeek,
    subMonths,
} from 'date-fns';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';
import Skeleton from '@mui/material/Skeleton';
import { useTranslation } from '@hooks';
import { I18N_DATE_FORMAT, isUnsetDate, isMaxDate, getWeekStartsOn } from '@helpers/dates';
import { CAMPAIGN_STATUS, CAMPAIGN_STATUS_TONE } from '@constants/social-posts';
import { STATUS_PALETTE } from '@components/StatusChip';
import Button from '@components/Button';
import ActionIconButton from '@components/ActionIconButton';
import PlatformIcon from '../SocialPostsTable/PlatformIcon';
import {
    CalendarWrapper,
    Toolbar,
    MonthTitle,
    NavGroup,
    WeekdayRow,
    WeekdayCell,
    Grid,
    DayCellRoot,
    DayNumber,
    ChipList,
    Chip,
    ChipTime,
    ChipTitle,
    ChipIcons,
    MoreButton,
} from './styles';

const MAX_VISIBLE_CHIPS = 3;

const dayKey = date => format(date, 'yyyy-MM-dd');

const isReschedulable = campaign => !!campaign?.canEdit && campaign.status !== CAMPAIGN_STATUS.COMPLETED;

const ChipView = forwardRef(({ campaign, locale, draggable, dragging, overlay, onClick, ...rest }, ref) => {
    const tone = STATUS_PALETTE[CAMPAIGN_STATUS_TONE[campaign.status] ?? 'neutral'];
    return (
        <Chip
            ref={ref}
            bg={tone.bg}
            fg={tone.fg}
            draggable={draggable}
            dragging={dragging}
            overlay={overlay}
            title={campaign.title}
            onClick={onClick}
            {...rest}
        >
            <ChipTime>{format(new Date(campaign.scheduledTime), 'HH:mm', { locale })}</ChipTime>
            <ChipTitle>{campaign.title}</ChipTitle>
            <ChipIcons>
                {campaign.platforms?.slice(0, 3).map(platform => (
                    <PlatformIcon key={platform} platform={platform} size={12} />
                ))}
            </ChipIcons>
        </Chip>
    );
});
ChipView.displayName = 'ChipView';

const CampaignChip = ({ campaign, locale, onOpen }) => {
    const draggable = isReschedulable(campaign);
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: campaign.id,
        data: { campaign },
        disabled: !draggable,
    });

    return (
        <ChipView
            ref={setNodeRef}
            campaign={campaign}
            locale={locale}
            draggable={draggable}
            dragging={isDragging}
            onClick={() => onOpen(campaign)}
            {...attributes}
            {...listeners}
        />
    );
};

const SkeletonCell = ({ day, month, locale }) => {
    const outside = !isSameMonth(day, month);
    return (
        <DayCellRoot outside={outside} creatable={false}>
            <DayNumber outside={outside}>{format(day, 'd', { locale })}</DayNumber>
            {!outside && (
                <ChipList>
                    <Skeleton variant="rounded" height={18} />
                    <Skeleton variant="rounded" height={18} width="60%" />
                </ChipList>
            )}
        </DayCellRoot>
    );
};

const DayCell = ({ day, month, campaigns, locale, onCreateOnDay, onOpen, t }) => {
    const [expanded, setExpanded] = useState(false);
    const { setNodeRef, isOver } = useDroppable({ id: dayKey(day), data: { day } });

    const creatable = !!onCreateOnDay && !isBefore(day, startOfDay(new Date()));
    const outside = !isSameMonth(day, month);
    const isToday = isSameDay(day, new Date());
    const visible = expanded ? campaigns : campaigns.slice(0, MAX_VISIBLE_CHIPS);
    const hiddenCount = campaigns.length - visible.length;

    const onCellClick = event => {
        if (creatable && event.target === event.currentTarget) onCreateOnDay(day);
    };

    return (
        <DayCellRoot
            ref={setNodeRef}
            outside={outside}
            isToday={isToday}
            isOver={isOver}
            creatable={creatable}
            onClick={onCellClick}
        >
            <DayNumber outside={outside} isToday={isToday}>
                {format(day, 'd', { locale })}
            </DayNumber>
            <ChipList onClick={onCellClick}>
                {visible.map(campaign => (
                    <CampaignChip key={campaign.id} campaign={campaign} locale={locale} onOpen={onOpen} />
                ))}
                {hiddenCount > 0 && (
                    <MoreButton
                        onClick={event => {
                            event.stopPropagation();
                            setExpanded(true);
                        }}
                    >
                        {t('SocialPosts.moreCount', { count: hiddenCount })}
                    </MoreButton>
                )}
            </ChipList>
        </DayCellRoot>
    );
};

const SocialPostsCalendar = ({ month, onMonthChange, onCreateOnDay, onClickSocialPost, onReschedule }) => {
    const { t } = useTranslation();
    const { data, loading } = useSelector(state => state.socialPosts);
    const language = useSelector(state => state.auth.user?.language);
    const accountLocale = useSelector(state => state.account?.account?.locale);

    const [activeCampaign, setActiveCampaign] = useState(null);

    const isInitialLoading = loading && !(data && data.length);

    const locale = I18N_DATE_FORMAT[language] || I18N_DATE_FORMAT.GB;
    const weekStartsOn = getWeekStartsOn(accountLocale);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const days = useMemo(() => {
        const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn });
        const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn });
        return eachDayOfInterval({ start: gridStart, end: gridEnd });
    }, [month, weekStartsOn]);

    const weekdays = useMemo(() => {
        const weekStart = startOfWeek(new Date(), { weekStartsOn });
        return Array.from({ length: 7 }, (_, i) => format(addDays(weekStart, i), 'EEE', { locale }));
    }, [weekStartsOn, locale]);

    const campaignsByDay = useMemo(() => {
        const map = {};
        (data || []).forEach(campaign => {
            if (isUnsetDate(campaign?.scheduledTime) || isMaxDate(campaign?.scheduledTime)) return;
            const key = dayKey(new Date(campaign.scheduledTime));
            (map[key] ||= []).push(campaign);
        });
        Object.values(map).forEach(list => list.sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime)));
        return map;
    }, [data]);

    const onOpen = campaign => onClickSocialPost(campaign, campaign.canEdit ? 'edit' : 'view');

    const onDragStart = ({ active }) => setActiveCampaign(active.data.current?.campaign ?? null);

    const onDragEnd = ({ active, over }) => {
        setActiveCampaign(null);
        if (!over) return;
        const campaign = active.data.current?.campaign;
        const targetDay = over.data.current?.day;
        if (!campaign || !targetDay || !isReschedulable(campaign)) return;

        const current = new Date(campaign.scheduledTime);
        if (isSameDay(current, targetDay)) return;

        const newDate = set(current, {
            year: targetDay.getFullYear(),
            month: targetDay.getMonth(),
            date: targetDay.getDate(),
        });
        if (isBefore(newDate, new Date())) return;

        onReschedule(campaign, newDate);
    };

    return (
        <DndContext
            sensors={sensors}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={() => setActiveCampaign(null)}
        >
            <CalendarWrapper>
                <Toolbar>
                    <MonthTitle>{format(month, 'LLLL yyyy', { locale })}</MonthTitle>
                    <NavGroup>
                        <Button variant="outlined" onClick={() => onMonthChange(startOfMonth(new Date()))}>
                            {t('common.today')}
                        </Button>
                        <ActionIconButton
                            title={t('common.previous')}
                            onClick={() => onMonthChange(subMonths(month, 1))}
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </ActionIconButton>
                        <ActionIconButton title={t('common.next')} onClick={() => onMonthChange(addMonths(month, 1))}>
                            <ChevronRightIcon fontSize="small" />
                        </ActionIconButton>
                    </NavGroup>
                </Toolbar>
                <WeekdayRow>
                    {weekdays.map(label => (
                        <WeekdayCell key={label}>{label}</WeekdayCell>
                    ))}
                </WeekdayRow>
                <Grid>
                    {days.map(day =>
                        isInitialLoading ? (
                            <SkeletonCell key={dayKey(day)} day={day} month={month} locale={locale} />
                        ) : (
                            <DayCell
                                key={dayKey(day)}
                                day={day}
                                month={month}
                                campaigns={campaignsByDay[dayKey(day)] || []}
                                locale={locale}
                                onCreateOnDay={onCreateOnDay}
                                onOpen={onOpen}
                                t={t}
                            />
                        ),
                    )}
                </Grid>
            </CalendarWrapper>
            <DragOverlay dropAnimation={null}>
                {activeCampaign ? <ChipView campaign={activeCampaign} locale={locale} overlay /> : null}
            </DragOverlay>
        </DndContext>
    );
};

export default SocialPostsCalendar;
